import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthToken, setRefreshToken, clearAuthTokens } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in (from localStorage)
    const checkAuthStatus = () => {
      try {
        const savedUser = localStorage.getItem('user');
        const savedAuth = localStorage.getItem('isAuthenticated');
        
        if (savedUser && savedAuth === 'true') {
          const userData = JSON.parse(savedUser);
          // Validate that the user data is still valid
          if (userData && userData.email && userData.id) {
            setUser(userData);
            setCompany(userData.company || null);
            setIsAuthenticated(true);
          } else {
            // Clear invalid data
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        // Clear corrupted data
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      const response = await authAPI.login({ email, password });
      
      if (response.success) {
        const { user: userData, token, refreshToken } = response.data;
        
        // Set tokens
        setAuthToken(token);
        setRefreshToken(refreshToken);
        
        // Set user and company state
        setUser(userData);
        setCompany(userData.company || null);
        setIsAuthenticated(true);
        
        // Save to localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('isAuthenticated', 'true');
        
        return userData; // Return user data for compatibility
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authAPI.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear everything regardless of API call success
      setUser(null);
      setCompany(null);
      setIsAuthenticated(false);
      clearAuthTokens();
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await authAPI.forgotPassword(email);
      return { message: response.message || 'Password reset link sent to your email' };
    } catch (error) {
      console.error('Forgot password error:', error);
      const message = error.response?.data?.message || 'Failed to send password reset email. Please try again.';
      throw new Error(message);
    }
  };

  // Helper functions for company information
  const getCompanyId = () => {
    return company?.companyId || user?.company?.companyId;
  };

  const getCompanyName = () => {
    return company?.companyName || user?.company?.companyName;
  };

  const getCompanyType = () => {
    return company?.companyType || user?.company?.companyType;
  };

  const getCompanySettings = () => {
    return company?.settings || user?.company?.settings;
  };

  const getCompanySubscription = () => {
    return company?.subscription || user?.company?.subscription;
  };

  const hasFeatureAccess = (featureName) => {
    const settings = getCompanySettings();
    return settings?.features?.[featureName] !== false;
  };

  const isCompanyActive = () => {
    return company?.isActive !== false && user?.company?.isActive !== false;
  };

  const value = {
    isAuthenticated,
    user,
    company,
    loading,
    login,
    logout,
    forgotPassword,
    // Company helper functions
    getCompanyId,
    getCompanyName,
    getCompanyType,
    getCompanySettings,
    getCompanySubscription,
    hasFeatureAccess,
    isCompanyActive
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
